import React from 'react';

import { CACHE_NAME } from '../../common/constants';
import { type ContextResolver } from '../../common/types';
import { getContext } from '../../common/utils/utils';
import { type DismissalData, type MutationResult, type UndoDismissalData } from '../graphql-result';
import { type CacheProviderConfig } from '../response-cache-provider';

import DismissRecommendationRequestMaker from './dismiss-recommendation-request-maker';
import {
	DismissRecommendationRequestBroker,
	UndoDismissalRequestBroker,
} from './dismissal-result/response-broker';
import {
	type DismissRecommendationResponse,
	type UndoDismissalResponse,
} from './dismissal-result/types';
import { Requester } from './requester';
import UndoDismissalRequestMaker from './undo-dismissal-request-maker';

export interface UseDismissalArgs {
	endpoint?: string;
	contextResolver: ContextResolver;
}

interface UseDismissal {
	dismissRecommendation: (productId: string) => Promise<MutationResult<DismissalData>>;
	undoDismissal: (productId: string) => Promise<MutationResult<UndoDismissalData>>;
}

const useDismissal = ({
	endpoint = '/gateway/api/graphql',
	contextResolver,
}: UseDismissalArgs): UseDismissal => {
	const context = getContext(contextResolver, false);

	const cacheConfig: CacheProviderConfig = React.useMemo(
		() => ({
			cacheName: CACHE_NAME,
			ttl: 0,
			useCase: context.useCase,
		}),
		[context.useCase],
	);

	/**
	 * Dismisses a recommendation for a given product.
	 */
	const dismissRecommendation = React.useCallback(
		(productId: string): Promise<MutationResult<DismissalData>> => {
			const dismissRecommendationRequestMaker = new DismissRecommendationRequestMaker(endpoint);

			const requester = new Requester<
				DismissRecommendationResponse,
				DismissRecommendationResponse['dismissRecommendation'],
				NonNullable<DismissRecommendationResponse['dismissRecommendation']['dismissal']>
			>(new DismissRecommendationRequestBroker());

			return requester.fetch(
				dismissRecommendationRequestMaker.makeRequest({
					productId,
					contextResolver,
				}),
				{
					cacheConfig,
				},
			);
		},
		[contextResolver, endpoint, cacheConfig],
	);

	/**
	 * Undoes a dismissal for a given product.
	 */
	const undoDismissal = React.useCallback(
		(productId: string): Promise<MutationResult<UndoDismissalData>> => {
			const undoDismissalRequestMaker = new UndoDismissalRequestMaker(endpoint);

			const requester = new Requester<
				UndoDismissalResponse,
				UndoDismissalResponse['undoDismissal'],
				NonNullable<UndoDismissalResponse['undoDismissal']['result']>
			>(new UndoDismissalRequestBroker());

			return requester.fetch(
				undoDismissalRequestMaker.makeRequest({ productId, contextResolver }),
				{
					cacheConfig,
				},
			);
		},
		[contextResolver, endpoint, cacheConfig],
	);

	return { dismissRecommendation, undoDismissal };
};

export default useDismissal;
